import {
    AfterContentInit,
    AfterViewInit,
    Component,
    ContentChild,
    DestroyRef,
    Directive,
    ElementRef,
    EventEmitter,
    HostListener,
    Input,
    OnDestroy,
    Output,
    ViewEncapsulation,
    computed,
    effect,
    inject, signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { NgControl } from '@angular/forms';
@Directive({
    selector: '[ce-input]',
})
export class ProjectedInputDirective {
    constructor(public elementRef: ElementRef) {}
}
@Component({
    selector: 'ce-form-field',
    imports: [CommonModule],
    templateUrl: './ce-form-field.component.html',
    styleUrl: './ce-form-field.component.scss',
    encapsulation: ViewEncapsulation.None,
})
export class CeFormFieldComponent implements AfterViewInit, OnDestroy, AfterContentInit {
    readonly #elementRef = inject(ElementRef);
    readonly #destroyRef = inject(DestroyRef);
    isInputFocused = signal<boolean>(false);
    inputValue = signal<string>('');
    // True once the user has typed during the current focus session (reset on focus/blur).
    // Drives the focus ring: 4px blue on plain focus, 2px brand once interaction starts.
    hasInteracted = signal<boolean>(false);
    isRequired = signal<boolean>(false);
    isDisabled = signal<boolean>(false);
    hasError = signal<boolean>(false);
    // default size is medium, no size class is added, however if size is set to 'sm' | 'lg' then we add a class to modify the input size
    inputSize = signal<'sm' | 'lg' | '' | 'md'>('');
    inputStatus = signal<string>('');

    #observer: MutationObserver | null = null;

    @Output() isFocused: EventEmitter<boolean> = new EventEmitter<boolean>();

    @Input()
    set size(value: 'sm' | 'lg' | 'md' | null) {
        if (value !== null) {
            this.inputSize.set(value);
        }
    }

    @Input() hideErrorSpace = false;

    @ContentChild(NgControl) ngControl!: NgControl;

    ngAfterContentInit() {
        if (this.ngControl) {
            this.ngControl.valueChanges?.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe((value) => {
                this.inputValue.set(value);
            });
            // Re-evaluate validity styling on status changes too — an async validator
            // (or programmatic markAsTouched/setErrors) resolves without any focus/input
            // event, so the isValid/isUnvalid class would otherwise stay stale (e.g. the
            // error outline lingering after a pending check passes).
            this.ngControl.statusChanges?.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe(() => {
                this.evalutateInteractionStatus();
            });
            // markAsUntouched()/markAsPristine()/reset() (e.g. resetting or re-seeding a
            // form) emit on `events` but not on statusChanges, so re-evaluate styling here
            // too. Clear the error outline once the control is no longer touched — otherwise
            // a previously-revealed error would linger after a programmatic reset, instead of
            // the field looking brand new.
            this.ngControl.control?.events?.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe(() => {
                if (!this.ngControl?.touched) {
                    this.hasError.set(false);
                }
                this.evalutateInteractionStatus();
            });
        }
    }

    @HostListener('focusin', ['$event.target'])
    onFocusIn(target: HTMLInputElement | HTMLTextAreaElement) {
        if (this.#isCeInputElement(target)) {
            this.isInputFocused.set(true);
            // Fresh focus session: show the 4px blue ring until the user types.
            this.hasInteracted.set(false);
            this.evalutateInteractionStatus();
        }
    }

    @HostListener('focusout', ['$event.target'])
    onFocusOut(target: HTMLInputElement | HTMLTextAreaElement) {
        if (this.#isCeInputElement(target)) {
            this.isInputFocused.set(false);
            this.hasInteracted.set(false);
            this.checkErrorOnBlur(target);
            this.evalutateInteractionStatus();
        }
    }

    @HostListener('input', ['$event.target'])
    onInput(target: HTMLInputElement | HTMLTextAreaElement) {
        if (this.#isCeInputElement(target)) {
            // First keystroke is an interaction: switch the focus ring from 4px blue
            // (just-focused) to 2px brand (typing), regardless of prior content.
            this.hasInteracted.set(true);
            this.inputValue.set(target.value);
            this.evalutateInteractionStatus();
        }
    }

    #isCeInputElement(target: HTMLElement): boolean {
        return (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA') && target.getAttribute('ce-input') !== null;
    }

    inputModifiers = computed(() => {
        return this.inputStatus() + ' ' + this.inputSize();
    });

    inputSelected() {
        const inputElement = this.#elementRef.nativeElement.querySelector('input[ce-input], textarea[ce-input]');
        if (inputElement) {
            inputElement.focus();
            this.isInputFocused.set(true);
        }
    }

    evalutateInteractionStatus() {
        if (this.ngControl !== undefined) {
            const inputStateAttributes = [
                this.ngControl.dirty ? 'isDirty' : 'isPristine',
                this.ngControl.touched ? 'isTouched' : 'isUntouched',
                this.ngControl.valid ? 'isValid' : 'isUnvalid',
                this.isInputFocused() ? 'isFocused' : 'isBlurred',
            ];
            this.inputStatus.set(inputStateAttributes.join(' '));
        }
    }

    checkErrorOnBlur(target: HTMLInputElement | HTMLTextAreaElement) {
        const classesArray = Array.from(target.classList);
        this.hasError.set(classesArray.includes('ng-invalid'));
    }

    onFocusBlurToggle = effect(
        () => {
            this.isFocused.emit(this.isInputFocused());
        },
        { allowSignalWrites: true }
    );

    formFieldClassComputed = computed(() => {
        let classNames = '';
        if (this.isInputFocused()) {
            // 4px blue (isFocused--empty) on plain focus; 2px brand (isFocused) once the
            // user has typed this session — independent of whether the field had content.
            classNames += this.hasInteracted() ? 'isFocused' : 'isFocused--empty';
        }

        if (this.isDisabled()) {
            classNames += ' isDisabled';
        }
        if (this.hasError()) {
            classNames += ' hasError';
        }
        return classNames;
    });

    evaluateAttributes(inputAttributes: { disabled: boolean; required: boolean }) {
        this.isDisabled.set(inputAttributes?.disabled);
        this.isRequired.set(inputAttributes?.required);
    }

    ngAfterViewInit() {
        const inputElement = this.#elementRef.nativeElement.querySelector('input, textarea');
        const inputAttributes = inputElement.attributes;
        this.evaluateAttributes(inputAttributes);

        // Watch for changes in the DOM attribute when a mutation occurs.
        this.#observer = new MutationObserver((mutationsList) => {
            for (const mutation of mutationsList) {
                if (mutation.type === 'attributes') {
                    this.evaluateAttributes(inputAttributes);
                }
            }
        });

        this.#observer.observe(inputElement, { attributes: true });
    }

    ngOnDestroy() {
        if (this.#observer) {
            this.#observer.disconnect();
        }
    }
}
